import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { FLOW_CONTROL_SOCKET_TYPE } from '@erp/workflows';

interface DragInfo {
  nodeId: string;
  handleId: string;
  handleType: 'source' | 'target';
  socketType: string; // e.g. 'string', 'entity_reference', 'json', 'any', 'flow', etc.
}

interface ConnectionDragContextType {
  isDragging: boolean;
  dragInfo: DragInfo | null;
  startDrag: (info: DragInfo) => void;
  endDrag: () => void;
  isCompatibleHandle: (
    nodeId: string,
    handleId: string,
    handleType: 'source' | 'target',
    socketType: string,
  ) => boolean;
}

/**
 * Check if two socket types are compatible for connection
 */
export function areSocketTypesCompatible(
  sourceType: string,
  targetType: string,
): boolean {
  // Flow control INPUT sockets accept ANY type (acts as a "wait for completion" signal)
  // This allows any output to trigger a flow control input
  if (targetType === FLOW_CONTROL_SOCKET_TYPE) {
    return true;
  }

  // Flow control OUTPUT sockets can ONLY connect to flow control inputs
  // (already handled above - if we get here, target is not flow control)
  if (sourceType === FLOW_CONTROL_SOCKET_TYPE) {
    return false;
  }

  // 'any' is compatible with everything
  if (sourceType === 'any' || targetType === 'any') {
    return true;
  }

  // Exact match
  if (sourceType === targetType) {
    return true;
  }

  // Arrays - check if element types match (must come before json/string checks)
  if (sourceType.startsWith('array<') && targetType.startsWith('array<')) {
    const sourceElementType = sourceType.slice(6, -1); // Extract 'string' from 'array<string>'
    const targetElementType = targetType.slice(6, -1);
    return areSocketTypesCompatible(sourceElementType, targetElementType);
  }

  // Array output to single input - allow connecting array<X> to X
  // This enables workflows where array items are processed individually or spread
  if (sourceType.startsWith('array<')) {
    const sourceElementType = sourceType.slice(6, -1); // Extract element type from 'array<X>'
    return areSocketTypesCompatible(sourceElementType, targetType);
  }

  // Single input to array output - allow connecting X to array<X>
  // This enables fan-in patterns where multiple sources combine into one array
  // The backend will concatenate the arrays
  if (targetType.startsWith('array<')) {
    const targetElementType = targetType.slice(6, -1); // Extract element type from 'array<X>'
    return areSocketTypesCompatible(sourceType, targetElementType);
  }

  // JSON can accept most types (it's a flexible container)
  if (targetType === 'json') {
    // Accept primitives and any entity_reference (typed or untyped)
    if (sourceType.startsWith('entity_reference')) {
      return true;
    }
    return ['string', 'number', 'boolean', 'json', 'uuid', 'date'].includes(
      sourceType,
    );
  }

  // String can accept many types (implicit conversion)
  if (targetType === 'string') {
    return ['string', 'number', 'boolean', 'uuid', 'date'].includes(sourceType);
  }

  // Entity reference compatibility - must match specific entity type
  // e.g., "entity_reference:ApInvoice" is only compatible with "entity_reference:ApInvoice"
  if (
    sourceType.startsWith('entity_reference') &&
    targetType.startsWith('entity_reference')
  ) {
    // If either is untyped (just "entity_reference"), allow connection
    if (
      sourceType === 'entity_reference' ||
      targetType === 'entity_reference'
    ) {
      return true;
    }
    // Both are typed - must match exactly
    return sourceType === targetType;
  }

  return false;
}

const ConnectionDragContext = createContext<ConnectionDragContextType | null>(
  null,
);

export function ConnectionDragProvider({ children }: { children: ReactNode }) {
  const [dragInfo, setDragInfo] = useState<DragInfo | null>(null);

  const startDrag = useCallback((info: DragInfo) => {
    setDragInfo(info);
  }, []);

  const endDrag = useCallback(() => {
    setDragInfo(null);
  }, []);

  const isCompatibleHandle = useCallback(
    (
      nodeId: string,
      handleId: string,
      handleType: 'source' | 'target',
      socketType: string,
    ) => {
      if (!dragInfo) return true;

      // Can't connect to the same node
      if (dragInfo.nodeId === nodeId) return false;

      // Source can only connect to target, and vice versa
      if (dragInfo.handleType === 'source' && handleType === 'source')
        return false;
      if (dragInfo.handleType === 'target' && handleType === 'target')
        return false;

      // Check socket type compatibility
      // If dragging from source (output), check if target (input) can accept the type
      // If dragging from target (input), check if source (output) can provide the type
      if (dragInfo.handleType === 'source') {
        // Dragging from output to input: source type -> target type
        return areSocketTypesCompatible(dragInfo.socketType, socketType);
      } else {
        // Dragging from input to output: source type -> target type (reversed)
        return areSocketTypesCompatible(socketType, dragInfo.socketType);
      }
    },
    [dragInfo],
  );

  const value = useMemo(
    () => ({
      isDragging: dragInfo !== null,
      dragInfo,
      startDrag,
      endDrag,
      isCompatibleHandle,
    }),
    [dragInfo, startDrag, endDrag, isCompatibleHandle],
  );

  return (
    <ConnectionDragContext.Provider value={value}>
      {children}
    </ConnectionDragContext.Provider>
  );
}

export function useConnectionDrag() {
  const context = useContext(ConnectionDragContext);
  if (!context) {
    // Return a no-op context for read-only mode
    return {
      isDragging: false,
      dragInfo: null,
      startDrag: (_info: DragInfo) => {},
      endDrag: () => {},
      isCompatibleHandle: () => true,
    };
  }
  return context;
}
